import React, { useEffect, useState, useCallback, useRef } from "react";
import "./HotelsCards.css";
import { BsChevronLeft, BsChevronRight } from "react-icons/bs";
import axios from "../../Utilities/axios";
import { useNavigate } from "react-router-dom";
import { RotatingLines } from "react-loader-spinner";
import HotelCardCarousel from "./HotelCardCarousel";
import { HOTEL_RATINGS } from "../../Utilities/googlePlaces";

const HotelsCards = () => {
  const [hotels, setHotels] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [page, setPage] = useState(0);

  // transition state
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionDirection, setTransitionDirection] = useState("next");
  const [exitHotel, setExitHotel] = useState(null);
  const [enterHotel, setEnterHotel] = useState(null);

  const navigation = useNavigate();
  const isAnimating = useRef(false);
  const transitionTimer = useRef(null);

  const preloadImages = useCallback((hotelList) => {
    const imageUrls = hotelList.map((h) => h.picture).filter(Boolean);

    let loadedCount = 0;
    const totalImages = imageUrls.length;

    if (totalImages === 0) {
      setImagesLoaded(true);
      return;
    }

    const checkAllLoaded = () => {
      loadedCount++;
      if (loadedCount === totalImages) {
        setImagesLoaded(true);
      }
    };

    imageUrls.forEach((url) => {
      const img = new Image();
      img.src = url;
      if (img.complete) {
        checkAllLoaded();
      } else {
        img.onload = checkAllLoaded;
        img.onerror = checkAllLoaded;
      }
    });
  }, []);

  const getHotels = async () => {
    setIsLoading(true);
    setImagesLoaded(false);
    try {
      const res = await axios.get(
        `/registrations/hotels?sourceType=showInCompanySite&hotelGroupName=Residency`,
      );
      setHotels(res.data);
      preloadImages(res.data);
    } catch {
      setHotels([]);
      setImagesLoaded(true);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    getHotels();
  }, []);

  const totalPages = hotels.length;
  const visibleHotel = hotels[page];

  /* ─────────────────────────────────────────────
     Animate to a new page
     Both cards exist in the DOM at the same time:
       • exit  layer : current card sliding out
       • enter layer : next card sliding in
     They animate simultaneously via CSS keyframes
     triggered by the `isTransitioning` class.
     ───────────────────────────────────────────── */
  const animateToPage = useCallback(
    (newPage, dir) => {
      if (isAnimating.current) return;
      if (newPage === page) return;
      if (!hotels[newPage]) return;

      isAnimating.current = true;

      setExitHotel(hotels[page]);
      setEnterHotel(hotels[newPage]);
      setTransitionDirection(dir);
      setIsTransitioning(true);

      transitionTimer.current = setTimeout(() => {
        setPage(newPage);
        setIsTransitioning(false);
        setExitHotel(null);
        setEnterHotel(null);
        isAnimating.current = false;
      }, 700); // must match CSS animation duration
    },
    [page, hotels],
  );

  const nextPage = useCallback(() => {
    const newPage = page === totalPages - 1 ? 0 : page + 1;
    animateToPage(newPage, "next");
  }, [page, totalPages, animateToPage]);

  const prevPage = useCallback(() => {
    const newPage = page === 0 ? totalPages - 1 : page - 1;
    animateToPage(newPage, "prev");
  }, [page, totalPages, animateToPage]);

  useEffect(() => {
    return () => {
      if (transitionTimer.current) clearTimeout(transitionTimer.current);
    };
  }, []);

  useEffect(() => {
    if (isLoading || !imagesLoaded || totalPages <= 1) return undefined;

    const interval = setInterval(nextPage, 5000);
    return () => clearInterval(interval);
  }, [isLoading, imagesLoaded, nextPage, totalPages]);

  const generateRandomCode = (length) => {
    const characters =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let code = "";
    for (let i = 0; i < length; i++) {
      code += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    return code;
  };

  const passDetails = (hotel) => {
    const randomCode = generateRandomCode(20);
    const hotelSlug = hotel.hotelName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
    navigation(`/hotel-rooms/${hotelSlug}/${hotel.hotelId}/${randomCode}`, {
      state: { hotel },
    });
  };

  const getHotelRating = (hotel) => {
    const name = (hotel.hotelName || "").toLowerCase();
    for (const [key, value] of Object.entries(HOTEL_RATINGS)) {
      if (
        key.toLowerCase().includes(name) ||
        name.includes(
          key
            .toLowerCase()
            .split(" ")
            .filter((w) => w.length > 3)
            .join(" "),
        )
      ) {
        return value;
      }
    }
    return null;
  };

  /* ── Render a single hotel card (used by both layers) ── */
  const renderCard = (hotel) => {
    if (!hotel) return null;
    const hotelRating = getHotelRating(hotel);

    return (
      <article className="hotels__card">
        <HotelCardCarousel
          hotelName={hotel.hotelName}
          apiImage={hotel.picture}
          location={hotel.location}
        />

        <div className="hotels__card-body">
          <div className="hotels__card-top">
            <h3 className="hotels__card-name">{hotel.hotelName}</h3>
          </div>

          <p className="hotels__card-address">{hotel.address}</p>

          {hotel.hotelDescription && (
            <p className="hotels__card-desc">{hotel.hotelDescription}</p>
          )}

          {hotelRating && (
            <div className="hotels__card-rating">
              <span className="hotels__card-rating-score">
                {hotelRating.rating.toFixed(1)}
              </span>
            </div>
          )}

          <button
            className="hotels__card-btn"
            onClick={() => passDetails(hotel)}
          >
            View Rooms
          </button>
        </div>
      </article>
    );
  };

  return (
    <div className="hotels">
      <div className="hotels__container">
        {isLoading || !imagesLoaded ? (
          <div className="hotels__loader">
            <RotatingLines
              strokeColor="#2e2d90"
              strokeWidth="4"
              animationDuration="0.75"
              width="64"
              visible={true}
            />
          </div>
        ) : (
          <div className="hotels__carousel">
            <button
              className="hotels__carousel-btn hotels__carousel-btn--prev"
              onClick={prevPage}
              aria-label="Previous hotel"
            >
              <BsChevronLeft />
            </button>

            <div
              className={`hotels__stage ${
                isTransitioning ? "hotels__stage--transitioning" : ""
              }`}
              data-direction={transitionDirection}
            >
              {/* Base layer — current card OR (during transition) the exiting card */}
              <div
                className={`hotels__layer ${
                  isTransitioning
                    ? "hotels__layer--exit"
                    : "hotels__layer--current"
                }`}
              >
                {isTransitioning
                  ? renderCard(exitHotel)
                  : renderCard(visibleHotel)}
              </div>

              {/* Enter layer — only mounted during a transition */}
              {isTransitioning && (
                <div className="hotels__layer hotels__layer--enter">
                  {renderCard(enterHotel)}
                </div>
              )}
            </div>

            <button
              className="hotels__carousel-btn hotels__carousel-btn--next"
              onClick={nextPage}
              aria-label="Next hotel"
            >
              <BsChevronRight />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default HotelsCards;